import React, { useState, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { Home, Users, User, Key } from 'react-feather';
import { Helmet } from 'react-helmet';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import qs from 'query-string';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { omit } from 'ramda';
import { useStorageState } from 'react-storage-hooks';
import QueueSettings from './queue-settings';
import { Position, Queue as QueueT } from '../store/types';
import {
  selectQueueById,
  selectPendingPositions,
  selectFulfilledPositions,
} from '../store/queues-slice';
import { selectPositionById } from '../store/positions-slice';
import {
  loadQueue,
  nextPosition,
  checkInPosition,
  rejectPosition,
  checkOutOldestPosition,
} from '../store/api';
import { AppState, AppDispatch } from '../store';
import Header from '../components/queue-header';
import Card from '../components/card';
import Button from '../components/button';
import CardLayout from '../components/card-layout';
import List from '../components/list';
import BottomSheet from '../components/bottom-sheet';
import Spinner from '../components/spinner';

dayjs.extend(relativeTime);

function useQuery<T = any>() {
  const { search } = useLocation();
  return (qs.parse(search) as unknown) as T;
}

function getNewestOldestPosition(positions: Position[]): [Position, Position] {
  const sortedPositions = positions.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return [sortedPositions[0], sortedPositions[sortedPositions.length - 1]];
}

function useAsyncCallback<T extends (...args: any[]) => Promise<any>>(
  asyncFn: T,
  deps: React.DependencyList,
) {
  const [loading, setLoading] = useState(false);
  const callback = useCallback((...args) => {
    setLoading(true);
    asyncFn(...args).finally(() => setLoading(false));
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [callback, loading] as const;
}

function QueueAuthenticated({
  params,
  pin,
  queue,
}: {
  params: { organization: string; queue: string };
  pin: string;
  queue: QueueT;
}) {
  const history = useHistory();
  const dispatch: AppDispatch = useDispatch();
  const [settings, setSettings] = useState(false);
  const [showQueueDetails, setShowQueueDetails] = useState(false);
  const query = useQuery<{ current: string }>();
  const pendingPositions = useSelector((state: AppState) =>
    selectPendingPositions(state, params.queue),
  );
  const fulfilledPositions = useSelector((state: AppState) =>
    selectFulfilledPositions(state, params.queue),
  );
  const currentPosition = useSelector((state: AppState) =>
    selectPositionById(state.positions, query.current),
  );
  const [onNextPosition, nextPositionLoading] = useAsyncCallback(
    () =>
      dispatch(nextPosition({ ...params, pin })).then((res) => {
        const id = Object.keys((res.payload as any).positions)[0];
        history.push({ search: qs.stringify({ ...query, current: id }) });
      }),
    [dispatch, params, query, pin, history],
  );
  const [onCheckInPosition, checkInPositionLoading] = useAsyncCallback(
    () =>
      dispatch(checkInPosition({ ...params, position: query.current, pin })).then(() => {
        history.push({ search: qs.stringify(omit(['current'], query)) });
      }),
    [dispatch, params, query, pin, history],
  );
  const [onRejectPosition, rejectPositionLoading] = useAsyncCallback(
    () =>
      dispatch(rejectPosition({ ...params, position: query.current, pin })).then(() => {
        history.push({ search: qs.stringify(omit(['current'], query)) });
      }),
    [dispatch, params, query, pin, history],
  );
  const [onCheckOutOldestPosition, checkOutOldestPositionLoading] = useAsyncCallback(
    () =>
      dispatch(checkOutOldestPosition({ ...params, pin })).then(() => {
        history.push({ search: qs.stringify(omit(['current'], query)) });
      }),
    [dispatch, params, query, pin, history],
  );

  const [newestPosition, oldestPosition] = getNewestOldestPosition(pendingPositions);

  const toggleSettings = () => {
    setSettings((settings) => !settings);
  };

  const toggleQueueDetails = () => {
    setShowQueueDetails((showQueueDetails) => !showQueueDetails);
  };

  return (
    <>
      <Helmet>
        <title>{queue.title}</title>
        <meta name="description" content={queue.description} />
      </Helmet>
      {settings && (
        <BottomSheet closeSheet={toggleSettings}>
          <Card>
            <QueueSettings queue={queue} pin={pin} />
          </Card>
        </BottomSheet>
      )}
      {showQueueDetails && (
        <BottomSheet closeSheet={toggleQueueDetails}>
          <Card fillAvailable fullWidth>
            <List>
              {pendingPositions.map((position) => (
                <li key={position.id}>
                  <span>{position.label}</span>
                  <span>{dayjs(position.createdAt).fromNow(true)}</span>
                </li>
              ))}
            </List>
          </Card>
        </BottomSheet>
      )}
      <Container>
        <Header
          status={
            queue.open ? 'success' : pendingPositions.length > 0 ? 'warning' : 'danger'
          }
          toggleSettings={toggleSettings}
        >
          {queue.title}
        </Header>
        <QueueManage>
          <Card fullWidth>
            <CardLayout largeIcon icon={<Home size={32} />}>
              <CardTitle>
                <strong>{fulfilledPositions.length}</strong> in the store
              </CardTitle>
            </CardLayout>
            <CheckoutWrapper>
              <Button
                danger
                disabled={fulfilledPositions.length < 1 || checkOutOldestPositionLoading}
                onClick={onCheckOutOldestPosition}
              >
                {checkOutOldestPositionLoading ? <Spinner /> : 'Check Out'}
              </Button>
            </CheckoutWrapper>
          </Card>
          <Card fullWidth>
            {currentPosition ? (
              <NowServing>
                <CardLayout largeIcon icon={<User size={32} />}>
                  <CardTitle>
                    Call <strong>{currentPosition.label}</strong> now
                  </CardTitle>
                </CardLayout>
                <List>
                  {/* <li>
                    Attempt
                    <span>
                      <strong>1</strong>
                    </span>
                  </li> */}
                  <li>
                    Waiting Time
                    <span>
                      <strong>{dayjs(currentPosition.createdAt).fromNow(true)}</strong>
                    </span>
                  </li>
                </List>
                <ButtonGroup>
                  <Button
                    danger
                    onClick={onRejectPosition}
                    disabled={rejectPositionLoading}
                  >
                    {rejectPositionLoading ? <Spinner /> : 'Remove'}
                  </Button>
                  {/* <Button warning>
                    Retry
                  </Button> */}
                  <Button onClick={onCheckInPosition} disabled={checkInPositionLoading}>
                    {checkInPositionLoading ? <Spinner /> : 'Check in'}
                  </Button>
                </ButtonGroup>
              </NowServing>
            ) : (
              <Idle>
                <CardLayout icon={<Users size={32} />}>
                  <CardTitle onClick={toggleQueueDetails}>
                    <strong>{pendingPositions.length}</strong> in the queue
                  </CardTitle>
                </CardLayout>
                <List>
                  <li>
                    Longest
                    {oldestPosition && (
                      <span>
                        <strong>{oldestPosition.label}</strong> (
                        {dayjs(oldestPosition.createdAt).fromNow(true)})
                      </span>
                    )}
                  </li>
                  <li>
                    Shortest
                    {newestPosition && (
                      <span>
                        <strong>{newestPosition.label}</strong> (
                        {dayjs(newestPosition.createdAt).fromNow(true)})
                      </span>
                    )}
                  </li>
                </List>
                <ButtonWrapper onClick={onNextPosition}>
                  <Button disabled={pendingPositions.length < 1 || nextPositionLoading}>
                    {nextPositionLoading ? <Spinner /> : 'Call Next'}
                  </Button>
                </ButtonWrapper>
              </Idle>
            )}
          </Card>
        </QueueManage>
      </Container>
    </>
  );
}

export default function Queue() {
  const dispatch: AppDispatch = useDispatch();
  const params = useParams<{ organization: string; queue: string }>();
  const [pin, setPin] = useStorageState(sessionStorage, `${params.organization}/pin`, '');
  const [pinInput, setPinInput] = useState('');
  const queue = useSelector((state: AppState) =>
    selectQueueById(state.queues, params.queue),
  );

  useEffect(() => {
    let promise = dispatch(loadQueue(params));

    const interval = setInterval(() => {
      promise = dispatch(loadQueue(params));
    }, 5000);

    return () => {
      clearInterval(interval);
      promise.abort();
    };
  }, [dispatch, params]);

  const onSubmitPin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPin(pinInput);
  };

  if (!pin) {
    const title = queue?.title ?? '';

    return (
      <Container>
        <PinForm onSubmit={onSubmitPin} autoComplete="off">
          <label>
            <LabelText>{title ? `Enter PIN for ${title}` : 'Enter PIN'}</LabelText>
            <KeyIcon>
              <Key />
            </KeyIcon>
            <input
              placeholder="------"
              type="text"
              name="pin"
              inputMode="numeric"
              pattern="[0-9]*"
              value={pinInput}
              onChange={(event) => setPinInput(event.target.value)}
            />
          </label>
          <Button>Continue</Button>
        </PinForm>
      </Container>
    );
  }

  if (queue) {
    return <QueueAuthenticated params={params} pin={pin} queue={queue} />;
  }

  return null;
}

const KeyIcon = styled('span')`
  position: absolute;
  bottom: 8px;
  left: 32px;
  z-index: 4;
`;
const LabelText = styled('span')`
  display: block;
  font-weight: bold;
  font-size: 24px;
  text-align: center;
  padding: 16px;
`;

const PinForm = styled('form')`
  padding: 16px;
  width: 100%;
  color: #fff;
  position: relative;

  label {
    position: relative;
    display: block;
    margin-bottom: 32px;
  }
  input {
    width: calc(100% - 64px);
    background: 0;
    border: 0;
    height: 48px;
    padding: 0 16px 0 32px;
    font-size: 32px;
    border-bottom: 2px solid #fff;
    color: #fff;
    outline: none;
    margin: 0 32px;
    letter-spacing: 4px;
    text-align: center;
    border-radius: 0;
  }
`;
const QueueManage = styled('div')`
  width: 100%;
  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto 1fr;
  margin-top: auto;
  /* flex: 1 100%; */

  @media (orientation: landscape) {
    grid-template-rows: auto;
    grid-template-columns: 50% 50%;
    grid-column-gap: 8px;
    flex: 1 auto;
    padding-right: 8px;
    margin-top: auto;
  }
`;

const Idle = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const NowServing = styled(Idle)``;

const CheckoutWrapper = styled('div')`
  display: block;
  padding: 0 16px 16px 16px;
  width: 100%;
`;
const ButtonWrapper = styled('div')`
  display: block;
  padding: 16px;
  width: 100%;
`;
const ButtonGroup = styled('div')`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  padding: 16px;
`;

const CardTitle = styled('div')`
  font-size: 24px;
  font-weight: bold;
`;

const Container = styled('div')`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #000;
  display: flex;
  flex-direction: column;
  padding-bottom: 8px;
  align-items: center;
  justify-content: center;
  @media (orientation: landscape) {
    height: auto;
    bottom: auto;
    max-height: 320px;
  }
`;
